/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import React, { useEffect, useState } from "react";

import type { TabProps } from "./Tab";

export const UseStateContext = React.createContext<{
	currentId: string;
	currentState: {
		left: number;
		width: number;
	};
	displayType: DesignStuff.Tabs.displayType;
	uiType: DesignStuff.Tabs.uiType;
	allActive?: boolean;
}>({
	currentId: "",
	currentState: {
		left: 0,
		width: 0
	},
	displayType: "normal",
	uiType: "normal",
	allActive: false
});
export const UseHandlerContext = React.createContext<DesignStuff.Tabs.SelectHandler<string> | null>(null);

export default function TabContainer({
	activeId,
	onSelected,
	displayType = "normal",
	uiType = "normal",
	allActive = false,
	children
}: {
	activeId: TabProps["activeId"];
	displayType?: DesignStuff.Tabs.displayType;
	uiType?: DesignStuff.Tabs.uiType;
	allActive?: boolean;
	onSelected?: DesignStuff.Tabs.SelectHandler<string>;
	children: React.ReactNode;
}) {
	const [currentId, setCurrentId] = useState<string>(activeId);
	const [currentState, setCurrentState] = useState({
		left: 0,
		width: 0
	});

	useEffect(() => {
		setCurrentState({
			left: handleState(currentId).left,
			width: handleState(currentId).width
		});
	}, [children]);

	useEffect(() => {
		if (currentId !== activeId) {
			setCurrentId(activeId);
		}
		handleSelectTab(activeId);
	}, [activeId]);

	const handleSelectTab = (id: string) => {
		if (currentId !== id) {
			onSelected?.(id);
			setCurrentId(id);
			setCurrentState({
				left: handleState(id).left,
				width: handleState(id).width
			});
		}
	};

	const handleState = (id: string): { left: number; width: number } => {
		const target = document.getElementById(id) as HTMLElement;

		return {
			left: target ? Math.ceil(target.offsetLeft) : 0,
			width: target ? Math.ceil(target.getBoundingClientRect().width) : 0
		};
	};

	return (
		<UseStateContext.Provider value={{ currentId, currentState, displayType, uiType, allActive }}>
			<UseHandlerContext.Provider value={handleSelectTab}>{children}</UseHandlerContext.Provider>
		</UseStateContext.Provider>
	);
}
